import firebase from 'fb/initconfig';
import AnandDate from 'helpers/AnandDate';
import { BulkComputeResult, BulkFieldDefinition } from 'helpers/bulkEdit/changePlan';
import { normalizeMobileNumber } from 'helpers/mobileNumber';
import { capitalizeName } from 'helpers/nameCase';
import { parseDateTime } from 'helpers/parseDateTime';

/**
 * The fields an Anumati bulk edit can target, and the typed operations allowed on each.
 *
 * These replace the old free text expressions (`value + 1`, `value+2d`, `value+3h`) that were
 * passed to `eval`, so every operation is explicit, validated up front and previewable.
 */

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_LABEL = 'YYYY-MM-DD hh:mm A';

function blank(value: any) {
	return value === undefined || value === null || value === '';
}

function formatText(value: any) {
	return blank(value) ? '—' : String(value);
}

function formatTimestamp(value: any) {
	if (!value?.seconds) {
		return '—';
	}

	return new AnandDate(value).format(DATE_TIME_LABEL);
}

function parseWholeNumber(input: string) {
	if (!/^-?\d+$/.test((input ?? '').trim())) {
		return null;
	}

	return parseInt(input.trim(), 10);
}

function requireAmount(input: string, { allowNegative = false } = {}) {
	let amount = parseWholeNumber(input);

	if (amount === null) {
		return 'Enter a whole number';
	}

	if (!allowNegative && amount <= 0) {
		return 'Enter a number greater than zero';
	}

	if (allowNegative && amount === 0) {
		return 'Enter a non zero number';
	}

	return null;
}

const nameField: BulkFieldDefinition = {
	key: 'name',
	field: 'name',
	label: 'Name',
	operations: [{ id: 'set', label: 'Set to', inputType: 'text', inputLabel: 'New name' }],
	formatValue: formatText,
	validateInput: (operation, input) => (input?.trim() ? null : 'Enter a name'),
	compute: (record, operation, input) => {
		// Matches the single record form, which capitalises names on save.
		let newValue = capitalizeName(input.trim());
		return newValue === record.name ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

const referenceField: BulkFieldDefinition = {
	key: 'reference',
	field: 'reference',
	label: 'Ref',
	operations: [{ id: 'set', label: 'Set to', inputType: 'text', inputLabel: 'New reference' }],
	formatValue: formatText,
	validateInput: (operation, input) => (input?.trim() ? null : 'Enter a reference'),
	compute: (record, operation, input) => {
		let newValue = input.trim();
		return newValue === record.reference ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

const phoneField: BulkFieldDefinition = {
	key: 'phone',
	field: 'phone',
	label: 'Mobile',
	operations: [{ id: 'set', label: 'Set to', inputType: 'text', inputLabel: 'New mobile number' }],
	formatValue: formatText,
	validateInput: (operation, input) => {
		if (!input?.trim()) {
			return 'Enter a mobile number';
		}

		return normalizeMobileNumber(input) || input.trim().startsWith('+') ? null : 'Enter a valid mobile number';
	},
	compute: (record, operation, input) => {
		let trimmed = input.trim();
		// Mirrors the normalisation the single record form applies.
		let newValue = normalizeMobileNumber(trimmed) ?? (trimmed.startsWith('+') ? trimmed : '+91' + trimmed);
		return newValue === record.phone ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

const countField: BulkFieldDefinition = {
	key: 'people_count',
	field: 'people_count',
	label: 'Count',
	operations: [
		{ id: 'set', label: 'Set to', inputType: 'number', inputLabel: 'New count' },
		{ id: 'increase', label: 'Increase by', inputType: 'number', inputLabel: 'Increase by' },
		{ id: 'decrease', label: 'Decrease by', inputType: 'number', inputLabel: 'Decrease by' },
	],
	formatValue: (value) => (blank(value) ? '—' : String(value)),
	validateInput: (operation, input) => {
		let amount = parseWholeNumber(input);

		if (amount === null) {
			return 'Enter a whole number';
		}

		if (operation === 'set') {
			return amount < 1 ? 'Count must be at least 1' : null;
		}

		return requireAmount(input);
	},
	compute: (record, operation, input) => {
		let amount = parseWholeNumber(input) ?? 0;

		if (operation === 'set') {
			return record.people_count === amount ? { status: 'unchanged' } : { status: 'change', newValue: amount };
		}

		let current = record.people_count;

		if (typeof current !== 'number' || isNaN(current)) {
			return { status: 'blocked', reason: 'No current count to adjust' };
		}

		let newValue = operation === 'increase' ? current + amount : current - amount;

		if (newValue < 1) {
			return { status: 'blocked', reason: 'Count would drop below 1' };
		}

		return newValue === current ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

const permissionDateField: BulkFieldDefinition = {
	key: 'permission_date',
	field: 'permission_date',
	label: 'Date',
	operations: [
		{ id: 'set', label: 'Set to', inputType: 'date', inputLabel: 'New date' },
		{ id: 'shiftDays', label: 'Shift by days', inputType: 'number', inputLabel: 'Days (use a minus sign to move earlier)' },
	],
	formatValue: formatText,
	validateInput: (operation, input) => {
		if (operation === 'shiftDays') {
			return requireAmount(input, { allowNegative: true });
		}

		if (!input?.trim() || isNaN(new Date(input).getTime())) {
			return 'Choose a valid date';
		}

		return null;
	},
	compute: (record, operation, input) => {
		let current = record.permission_date;

		if (operation === 'set') {
			let newValue = new AnandDate(input.trim()).format(DATE_FORMAT);
			return newValue === current ? { status: 'unchanged' } : { status: 'change', newValue };
		}

		if (blank(current)) {
			return { status: 'blocked', reason: 'No current date to shift' };
		}

		let days = parseWholeNumber(input) ?? 0;
		let newValue = new AnandDate(current).add(days, 'day').format(DATE_FORMAT);

		return newValue === current ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

function timestampField(field: string, label: string): BulkFieldDefinition {
	return {
		key: field,
		field,
		label,
		operations: [
			{ id: 'set', label: 'Set to', inputType: 'datetime', inputLabel: `New ${label.toLowerCase()}` },
			{ id: 'shiftDays', label: 'Shift by days', inputType: 'number', inputLabel: 'Days (use a minus sign to move earlier)' },
			{ id: 'shiftHours', label: 'Shift by hours', inputType: 'number', inputLabel: 'Hours (use a minus sign to move earlier)' },
		],
		formatValue: formatTimestamp,
		validateInput: (operation, input) => {
			if (operation === 'set') {
				return parseDateTime(input) ? null : 'Choose a valid date and time';
			}

			return requireAmount(input, { allowNegative: true });
		},
		compute: (record, operation, input): BulkComputeResult => {
			let current = record[field];

			if (operation === 'set') {
				let parsed = parseDateTime(input);

				if (!parsed) {
					return { status: 'blocked', reason: 'Invalid date and time' };
				}

				let newValue = firebase.firestore.Timestamp.fromDate(parsed);
				return newValue.seconds === current?.seconds ? { status: 'unchanged' } : { status: 'change', newValue };
			}

			if (!current?.seconds) {
				return { status: 'blocked', reason: `No current ${label.toLowerCase()} to shift` };
			}

			let amount = parseWholeNumber(input) ?? 0;
			let shifted = new AnandDate(current).add(amount, operation === 'shiftHours' ? 'hour' : 'day');
			let newValue = firebase.firestore.Timestamp.fromDate(shifted.getDateObj());

			return newValue.seconds === current.seconds ? { status: 'unchanged' } : { status: 'change', newValue };
		},
	};
}

const approvalField: BulkFieldDefinition = {
	key: 'approval',
	field: 'approval',
	label: 'Approval',
	operations: [
		{ id: 'approve', label: 'Approve' },
		{ id: 'reject', label: 'Reject' },
	],
	formatValue: (value) => (value?.status ? String(value.status) : '—'),
	compute: (record, operation) => {
		// Matches the existing rule: a card that has already been issued is settled.
		if (record.card_no) {
			return { status: 'blocked', reason: 'Card already issued' };
		}

		if (!record.approval) {
			return { status: 'blocked', reason: 'Not a card request' };
		}

		let status = operation === 'approve' ? 'Approved' : 'Rejected';

		if (record.approval.status === status) {
			return { status: 'unchanged' };
		}

		return { status: 'change', newValue: { ...record.approval, status } };
	},
};

const deletedField: BulkFieldDefinition = {
	key: 'deleted',
	field: 'deleted',
	label: 'Deleted',
	operations: [
		{ id: 'softDelete', label: 'Move to deleted' },
		{ id: 'restore', label: 'Restore' },
	],
	formatValue: (value) => (value === true ? 'Deleted' : 'Not deleted'),
	compute: (record, operation) => {
		let newValue = operation === 'softDelete';
		return !!record.deleted === newValue ? { status: 'unchanged' } : { status: 'change', newValue };
	},
};

const permanentDeleteField: BulkFieldDefinition = {
	key: 'permanentDelete',
	field: 'deleted',
	label: 'Permanently delete',
	operations: [{ id: 'permanentDelete', label: 'Delete permanently', destructive: true }],
	formatValue: () => 'Exists',
	compute: () => ({ status: 'change', remove: true, newValue: true }),
};

export const anumatiBulkFields: BulkFieldDefinition[] = [
	nameField,
	countField,
	permissionDateField,
	referenceField,
	phoneField,
	timestampField('publishTime', 'Publish Time'),
	timestampField('expireTime', 'Expire Time'),
	approvalField,
	deletedField,
	permanentDeleteField,
];

export function findBulkField(key: string) {
	return anumatiBulkFields.find((definition) => definition.key === key);
}
